import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AppRoutes } from 'navigations/app-routes'
import { StyleSheet } from 'react-native'
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import i18n from "utils/i18n";
import { useNavigation } from '@react-navigation/native'
import _ from 'lodash'
import { constantsActive, constantsGraphql } from 'utils/constants/constants'
import { BottomTabBarHeightContext } from '@react-navigation/bottom-tabs'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import FetchCachedMessage from 'utils/fetch/cached/FetchCachedMessage'
import ThemedLoader from 'components/atoms/ThemedLoader'
import ThemedList from 'components/atoms/ThemedList'
import ThemedItem from 'components/atoms/ThemedItem'
import DataItem from 'components/atoms/DataItem'
import { colorList } from 'utils/colors'
import { messageConstants } from 'utils/constants/message-constants'
import { mutateReadOne } from './_functions/messages'
import { useSelector } from 'react-redux'
import { dataConstants } from 'services/redux/data/constants'
import NoResultsView from 'components/atoms/NoResultsView'
import HelperString from 'utils/helper-string'
import IconContainer from 'components/molecules/IconContainer'
import ThemedButtonContainer from 'components/atoms/ThemedButtonContainer'
import Button from 'theme/Button'
import MutateMessage from 'utils/mutate/message'
import ThemedPressable from 'components/atoms/ThemedPressable'
import Icon from 'components/atoms/Icon'
import { IconSystemConstants } from 'utils/constants/IconNameConstants'
import { TESTID } from '_shared/config/testID'
import HelperDate, { dateFormat } from 'utils/helper-date'

const MODES = {
	ACTIVE: 'active',
	ARCHIVED: 'archived'
}

const HomeMoreInboxScene = () => {
	const navigation = useNavigation();
	const tabBarHeight = useContext( BottomTabBarHeightContext ) ?? 0;
	const insets = useSafeAreaInsets();
	
	const [ mode, setMode ] = useState( MODES.ACTIVE )
	const [ loading, setLoading ] = useState( false );
	const [ loadingMore, setLoadingMore ] = useState( false );
	const [ marking, setMarking ] = useState( false )
	const [ archiving, setArchiving ] = useState( false )
	
	const _messages = useSelector( state => {
		return state.data[dataConstants.MESSAGES];
	} )
	
	const messages = useMemo( () => {
		return (
			_.orderBy(
				_.filter( _messages, message => {
					return mode !== MODES.ACTIVE || message.active !== constantsActive.NO;
				} ),
				[ 'created_at' ], [ 'desc' ],
			)
		);
	}, [ _messages, mode ] );

	const reloadMessages = useCallback( () => {
		FetchCachedMessage.reload({
			callbackStart: setLoading.bind( this, true ),
			callbackSuccess: setLoading.bind( this, false ),
			callbackError: setLoading.bind( this, false )
		});
	}, [] );

	useEffect( () => {
		FetchCachedMessage.variables = {
			...FetchCachedMessage.variables,
			active: constantsGraphql.YES
		};
		reloadMessages();
	}, [] );
	
	const openMessage = useCallback( ( item ) => {
		if( item.is_read === constantsActive.NO ) {
			mutateReadOne( item.guid ); // mutate as read here :-)
		}
	
		navigation.navigate( AppRoutes.MORE_INBOX_VIEW, {
			guid: _.get( item, 'guid' ),
			type: messageConstants.MESSAGE
		});
	}, [ navigation ] );
	
	const renderItem = useCallback( ( { item } ) => {
		const size = 40
		
		let titleStyle = styles.ThemedItemTitleStyleRead;
		let icon = <Icon icon={ IconSystemConstants.ENVELOPE } checked={ true } style={ [ styles.DataItemIconRead ] } />;
		let iconGradient = [
			colorList.GREY.primary,
			colorList.GREY.light,
		]
		
		if( item.is_read === constantsActive.NO ) {
			titleStyle = styles.ThemedItemTitleStyleUnread;
			icon = <Icon icon={ IconSystemConstants.ENVELOPE } style={ [ styles.DataItemIconUnRead ] } />;
			iconGradient = [
				colorList.PRIMARY.lighter,
				colorList.PRIMARY.lighter,
			]
		}
		
		const iconContainer = (
			<IconContainer
				backgroundIcons={ false }
				linearGradientColours={ iconGradient }
				icon={ icon }
				size={ size }
			/>
		);
		
		const dataItems = [
			<DataItem
				key={ HelperString.hash('inbox', item ) }
				content={ HelperDate.date( item.created_at ).format( dateFormat.DATE_TIME ) }
				textStyle={ styles.DataItemTextStyle }
			/>
		];
	
		return (
			<ThemedPressable
				key={ HelperString.hash('inbox-item', item ) }
				style={ styles.messageRow }
				onPress={ openMessage.bind( this, item ) }>
				
				<ThemedItem
					key={ HelperString.hash( 'inbox-item-view', item ) }
					identifier={ HelperString.hash( 'inbox-item-view', item ) }
					style={ styles.messageRow }
					accessoryLeft={ iconContainer }
					accessoryLeftSize={ size }
					title={ item.title }
					items={ dataItems }
					titleStyle={ titleStyle }
				/>
			</ThemedPressable>
		);
	}, [ openMessage ] );
	
	const onMarkAllAsRead = () => {
		MutateMessage.readAll({
			callbackStart: setMarking.bind( this, true ),
			callbackError: setMarking.bind( this, false ),
			callbackSuccess: setMarking.bind( this, false ),
		})
	}
	
	const onArchiveAll = () => {
		MutateMessage.archiveAll({
			callbackStart: setArchiving.bind( this, true ),
			callbackError: setArchiving.bind( this, false ),
			callbackSuccess: setArchiving.bind( this, false ),
		})
	}
	
	const onViewArchived = () => {
		setMode( MODES.ARCHIVED )
		
		FetchCachedMessage.variables = {
			...FetchCachedMessage.variables,
			active: constantsGraphql.YES_NO
		}
		
		FetchCachedMessage.fetchMore({
			callbackStart: setLoadingMore.bind( this, true ),
			callbackError: setLoadingMore.bind( this, false ),
			callbackSuccess: setLoadingMore.bind( this, false ),
		})
	}
	
	const hasUnread = messages.some( message => {
		return message.is_read === constantsActive.NO;
	} );
	const hasActive = messages.some( message => {
		return message.active === constantsActive.YES;
	} );

	const header = messages.length > 0 ? (
		<ThemedButtonContainer>
			<Button status="basic" size="small" style={ styles.actionButton }
				isLoading={ marking } disabled={ !hasUnread || marking } onPress={ onMarkAllAsRead }>
				{ i18n.t( 'page.message.action.read_all' ) }
			</Button>
			<Button status="basic" size="small" style={ styles.actionButton }
				isLoading={ archiving } disabled={ !hasActive || archiving } onPress={ onArchiveAll }>
				{ i18n.t( 'page.message.action.archive_all' ) }
			</Button>
		</ThemedButtonContainer>
	) : null;

	const footer = (
		<ThemedButtonContainer>
			<Button status="basic" size="small" style={ styles.archiveButton }
				isLoading={ loadingMore } disabled={ loading || loadingMore } onPress={ onViewArchived }>
				{ mode === MODES.ARCHIVED ? i18n.t( 'basic.load_more' ) : i18n.t( 'basic.archive' ) }
			</Button>
		</ThemedButtonContainer>
	);

	return (
		<ThemedSafeAreaComponentTop top={false}>
			<ThemedLayout
				contentWidth="narrow"
				topNavigation showBackButton enableScrollView={ false }
				title={ i18n.t( 'titles.more_inbox' ) }
				testID={ TESTID.HOME.MORE.SCREENS.INBOX }>
				<ThemedList
					scrollEnabled
					data={ messages }
					renderItem={ renderItem }
					style={ styles.list }
					estimatedItemSize={ 70 }
					refreshing={ loading }
					onRefresh={ reloadMessages }
					ListHeaderComponent={ header }
					ListFooterComponent={ footer }
					ListEmptyComponent={ loading
						? <ThemedLoader text={ i18n.t( 'loading.messages' ) } />
						: <NoResultsView>{ i18n.t( 'page.message.empty' ) }</NoResultsView> }
					contentContainerStyle={{ paddingBottom: tabBarHeight > 0 ? tabBarHeight + 32 : Math.max( insets.bottom, 15 ) }}
				/>
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	);
}

const styles = StyleSheet.create({
	list: { paddingHorizontal: 0 },
	actionButton: { flex: 1 },
	archiveButton: { width: '75%' },
	messageRow: {
		flex: 0,
		backgroundColor: colorList.WHITE.primary,
	},
	ThemedItemTitleStyleUnread: {
		color: colorList.ORANGE.primary
	},
	ThemedItemTitleStyleRead: {
		fontWeight: 'normal'
	},
	DataItemIconUnRead: {
		color: colorList.ORANGE.primary
	},
	DataItemIconRead: {
		color: colorList.BLACK.light
	},
	DataItemTextStyle: {
		marginLeft: 0,
		color: colorList.BLACK.light
	}
})

export default HomeMoreInboxScene;
